import React, { useEffect, useState } from 'react';
import { ExternalLink, Paperclip, Mail, Lock, Trash2 } from 'lucide-react';
import Modal from './Modal';
import StatusBadge from './StatusBadge';
import RequestConversation from './RequestConversation';
import { api, ApiError } from '../../lib/api';

const inputClasses =
  'w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.05] transition-all';
const labelClasses = 'block text-[11px] font-medium text-gray-400/80 mb-1.5 tracking-wide';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', tone: 'accent' },
  { value: 'in_progress', label: 'In progress', tone: 'warning' },
  { value: 'awaiting_reply', label: 'Awaiting reply', tone: 'waiting' },
  { value: 'resolved', label: 'Resolved', tone: 'success' },
  { value: 'closed', label: 'Closed', tone: 'muted' },
];

const PRIORITY_TONES = {
  low: 'neutral',
  medium: 'warning',
  high: 'warning',
  critical: 'danger',
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function attachmentHref(key) {
  const segments = key.split('/').map(encodeURIComponent).join('/');
  return `/api/attachments/${segments}`;
}

export default function TicketDetailModal({ open, onClose, ticket, onSaved, onDeleted }) {
  const [status, setStatus] = useState('open');
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && ticket) {
      setStatus(ticket.status || 'open');
      setAdminNotes(ticket.adminNotes || '');
      setError('');
      setSaving(false);
      setDeleting(false);
      setConfirmDelete(false);
    }
  }, [open, ticket]);

  // A message sent from the conversation can move the ticket to Awaiting reply
  // (or a reply can clear it): mirror that here and in the list.
  const onThread = (t) => {
    if (!t || !ticket) return;
    setStatus(t.status);
    onSaved?.({ id: ticket.id, status: t.status, awaitingReplySince: t.awaitingReplySince, newReply: t.newReply, lastReplyAt: t.lastReplyAt, conversationId: t.conversationId });
  };

  const onSave = async () => {
    if (!ticket) return;
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const data = await api.put(`/api/admin/tickets/${ticket.id}`, {
        status,
        adminNotes,
      });
      onSaved?.(data.ticket);
      setSaving(false);
      onClose?.();
    } catch (err) {
      setSaving(false);
      setError(err instanceof ApiError ? err.message : 'Failed to save');
    }
  };

  // Admin/ops soft-delete (any status). Hidden everywhere afterward; restorable in D1.
  const onDelete = async () => {
    if (!ticket || deleting) return;
    setDeleting(true);
    setError('');
    try {
      await api.delete(`/api/admin/tickets/${ticket.id}`);
      onDeleted?.(ticket.id);
      setDeleting(false);
      onClose?.();
    } catch (err) {
      setDeleting(false);
      setError(err instanceof ApiError ? err.message : 'Failed to delete');
    }
  };

  if (!ticket) return null;

  return (
    <Modal
      open={open}
      onClose={saving ? () => {} : onClose}
      title={ticket.title}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex items-center justify-between gap-3">
          {confirmDelete ? (
            <span className="inline-flex items-center gap-2 text-sm">
              <span className="text-gray-400">Delete this ticket?</span>
              <button type="button" onClick={onDelete} disabled={deleting} className="px-3 py-2 rounded-full bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25 transition-all disabled:opacity-60">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting} className="px-3 py-2 rounded-full border border-white/[0.1] text-sm text-gray-400 hover:text-white transition-all disabled:opacity-60">
                Cancel
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirmDelete(true)} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-gray-400 hover:text-red-300 hover:bg-red-500/10 transition-all disabled:opacity-60" title="Delete ticket">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving || deleting}
              className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || deleting}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <span className="w-3.5 h-3.5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                  Saving…
                </span>
              ) : (
                'Save changes'
              )}
            </button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge tone={PRIORITY_TONES[ticket.priority] || 'neutral'}>
            {ticket.priority}
          </StatusBadge>
          <span className="text-[12px] text-gray-500">
            {ticket.orgName} · by {ticket.submittedBy?.name || 'unknown'}
            {ticket.submittedBy?.email && (
              <a
                href={`mailto:${ticket.submittedBy.email}`}
                className="ml-1.5 inline-flex items-center gap-1 text-gray-400 hover:text-white transition-colors"
              >
                <Mail className="w-3 h-3" />
                {ticket.submittedBy.email}
              </a>
            )}
          </span>
        </div>

        <div>
          <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">
            Description
          </div>
          <div className="px-4 py-3 rounded-lg bg-white/[0.02] border border-white/[0.05] text-[13px] text-gray-300 leading-relaxed whitespace-pre-wrap">
            {ticket.description}
          </div>
        </div>

        {ticket.affectedUrl && (
          <div>
            <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">
              Affected URL
            </div>
            <a
              href={ticket.affectedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[13px] text-[#B0FC38] hover:text-white transition-colors break-all"
            >
              {ticket.affectedUrl}
              <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </div>
        )}

        {ticket.attachments && ticket.attachments.length > 0 && (
          <div>
            <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">
              Attachments ({ticket.attachments.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {ticket.attachments.map((a) => (
                <a
                  key={a.key}
                  href={attachmentHref(a.key)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 hover:bg-white/[0.05] hover:border-white/[0.15] transition-all"
                >
                  <Paperclip className="w-3 h-3 text-gray-500" />
                  <span className="truncate max-w-[200px]">{a.filename}</span>
                  <span className="text-gray-600">{formatBytes(a.size)}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="pt-3 border-t border-white/[0.06]">
          <label className={labelClasses} htmlFor="ticket-status">Status</label>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((s) => {
              const selected = status === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setStatus(s.value)}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-all ${
                    selected
                      ? 'bg-[#B0FC38]/[0.15] text-white border border-[#B0FC38]/40'
                      : 'bg-white/[0.02] text-gray-400 border border-white/[0.06] hover:border-white/[0.12]'
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <RequestConversation kind="ticket" itemId={ticket.id} onThread={onThread} />

        <div>
          <label className={labelClasses} htmlFor="admin-notes">
            <span className="inline-flex items-center gap-1.5">
              <Lock className="w-3 h-3" />
              Internal notes
              <span className="text-gray-600 ml-1">(not visible to client)</span>
            </span>
          </label>
          <textarea
            id="admin-notes"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            rows={4}
            className={`${inputClasses} resize-y min-h-[80px]`}
            placeholder="Notes for the NexSav team: investigation steps, blockers, coordination details."
          />
        </div>

        {error && (
          <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
            {error}
          </div>
        )}

        <div className="pt-3 border-t border-white/[0.04] flex items-center gap-4 text-[11px] text-gray-500">
          <span>Submitted {formatDate(ticket.createdAt)}</span>
          {ticket.updatedAt && ticket.updatedAt !== ticket.createdAt && (
            <span>· Updated {formatDate(ticket.updatedAt)}</span>
          )}
        </div>
      </div>
    </Modal>
  );
}
